import type * as Sentry from "@sentry/react";
import type { FocusedDocumentAttachment } from "@common/types/agent-chat-request";
import { officeWordDocument } from "./anchor/anchor-tracker-registry";
import { fetchDocName } from "./document";
import { DocumentReadProgress } from "./document-read-progress";
import { readDocumentForUpload, type UploadReadTelemetry } from "./document-upload-guard";
import { toError } from "./errors";
import { withLatencyWarning } from "./performance";

export interface FocusedDocumentSnapshot {
    fileBase64: string | null;
    fileName: string;
    coversEventsThroughSequence: number;
}

export interface FocusedDocumentAttachmentRead {
    attachment: FocusedDocumentAttachment;
    coversEventsThroughSequence: number;
}

interface InFlightFocusedDocumentRead {
    snapshot: Promise<FocusedDocumentSnapshot>;
    progress: DocumentReadProgress;
}

export interface FocusedDocumentRead extends InFlightFocusedDocumentRead {
    reusedInFlightRead: boolean;
}

let inFlightRead: InFlightFocusedDocumentRead | null = null;

export function joinFocusedDocumentRead(telemetry: UploadReadTelemetry, requesterSpan: Sentry.Span | null): FocusedDocumentRead {
    if (inFlightRead) {
        requesterSpan?.setAttribute("reused_in_flight_read", true);
        requesterSpan?.addLink({ context: inFlightRead.progress.readSpan.spanContext() });
        return { ...inFlightRead, reusedInFlightRead: true };
    }

    requesterSpan?.setAttribute("reused_in_flight_read", false);
    const progress = new DocumentReadProgress(requesterSpan);
    const read: InFlightFocusedDocumentRead = { snapshot: readFocusedDocumentSnapshot(telemetry, progress), progress };
    inFlightRead = read;
    const settle = () => {
        progress.finish();
        if (inFlightRead === read) inFlightRead = null;
    };
    read.snapshot.then(settle, settle);
    return { ...read, reusedInFlightRead: false };
}

async function readFocusedDocumentSnapshot(
    telemetry: UploadReadTelemetry,
    progress: DocumentReadProgress,
): Promise<FocusedDocumentSnapshot> {
    const coversEventsThroughSequence = officeWordDocument.events.lastEventSequence;
    const [fileBase64, fileName] = await Promise.all([
        readDocumentForUpload(telemetry, progress).then((fileBase64) => {
            progress.enterStep("await_document_name");
            return fileBase64;
        }),
        withLatencyWarning(`${telemetry.action}:fetch_doc_name`, fetchDocName),
    ]);
    return { fileBase64, fileName, coversEventsThroughSequence };
}

export async function readFocusedDocumentAttachment(
    telemetry: UploadReadTelemetry,
): Promise<FocusedDocumentAttachmentRead | undefined> {
    try {
        const snapshot = await joinFocusedDocumentRead(telemetry, null).snapshot;
        if (snapshot.fileBase64 === null) return undefined;
        return {
            attachment: { file_name: snapshot.fileName, docx_base64: snapshot.fileBase64 },
            coversEventsThroughSequence: snapshot.coversEventsThroughSequence,
        };
    } catch (err) {
        telemetry.sendError(toError(err), {
            component: telemetry.component,
            action: `${telemetry.action}:document_read`,
        });
        return undefined;
    }
}
